import type { ChartConfig } from '@/shared/components/ui/chart'
import type { CSSProperties } from 'react'
import type { ChartSerie } from '../model/types'

import { useMemo } from 'react'
import { format } from 'date-fns'
import orderBy from 'lodash/fp/orderBy'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'

import { durations } from '@/shared/components/Chart/utils'
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

type ChartRow = { date: number } & Record<string, number | null>

const MY_PRICE_SERIE = 'My price'

// Highcharts fed each serie its own [timestamp, value] tuples. Recharts wants a
// single row per x-value, so we pivot the (already aligned) series back into
// rows keyed by timestamp.
const toRows = (series: ChartSerie[]): ChartRow[] => {
	const rows = new Map<number, ChartRow>()

	for (const serie of series) {
		for (const [timestamp, value] of serie.data) {
			const row = rows.get(timestamp) ?? { date: timestamp }
			row[serie.name] = value
			rows.set(timestamp, row)
		}
	}

	return orderBy(['date'], ['asc'], Array.from(rows.values()))
}

const toConfig = (series: ChartSerie[]): ChartConfig =>
	Object.fromEntries(
		series.map((serie) => [
			serie.name,
			{ label: serie.name, color: serie.color },
		])
	)

// 5-day ticks, matching the original `tickInterval: durations.day * 5`.
const toTicks = (rows: ChartRow[]): number[] => {
	if (rows.length === 0) return []

	const start = rows[0].date
	const end = rows[rows.length - 1].date
	const ticks: number[] = []

	for (let tick = start; tick <= end; tick += durations.day * 5) {
		ticks.push(tick)
	}

	return ticks
}

export default function PriceHistoryChart({
	series,
}: {
	series: ChartSerie[]
}) {
	const chartConfig = useMemo(() => toConfig(series), [series])
	const rows = toRows(series)
	const ticks = toTicks(rows)

	return (
		<div className='pl-2 fade-in'>
			<ChartContainer config={chartConfig} className='h-50 w-full'>
				<LineChart
					accessibilityLayer={false}
					data={rows}
					margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
				>
					<CartesianGrid vertical={false} strokeDasharray='3 3' />
					<XAxis
						dataKey='date'
						scale='time'
						domain={['dataMin', 'dataMax']}
						ticks={ticks}
						type='number'
						fontSize={10}
						tickLine={false}
						axisLine={false}
						tickFormatter={(value) => format(new Date(value), 'dd MMM')}
					/>
					<YAxis
						fontSize={10}
						tickLine={false}
						axisLine={false}
						domain={['dataMin', 'dataMax']}
						width={40}
					/>
					<ChartTooltip
						cursor={{ strokeDasharray: '3 3' }}
						content={
							<ChartTooltipContent
								labelFormatter={(value) =>
									typeof value === 'string' || typeof value === 'number'
										? format(new Date(value), 'dd MMM yyyy')
										: ''
								}
								formatter={(value, name, item) => (
									<>
										<div
											className='h-2.5 w-2.5 shrink-0 rounded-[2px] bg-(--color-bg)'
											style={
												{
													'--color-bg': item.color ?? item.fill,
												} as CSSProperties
											}
										/>
										<div className='flex flex-1 items-center justify-between leading-none'>
											<div className='grid gap-1.5'>
												<span className='text-background/50'>
													{chartConfig[name as keyof typeof chartConfig]
														?.label ?? name}
												</span>
											</div>
											{item.value && (
												<span className='ml-1 font-mono font-medium text-background tabular-nums'>
													{formatNumber(value as number, NumberFormats.Fixed)}
												</span>
											)}
										</div>
									</>
								)}
							/>
						}
					/>
					{series.map((serie) => (
						<Line
							key={serie.name}
							dataKey={serie.name}
							name={serie.name}
							stroke={serie.color}
							strokeWidth={1}
							connectNulls
							dot={{ r: 2, strokeWidth: 1, fill: serie.color }}
							activeDot={{ r: 3 }}
							type='linear'
						/>
					))}
				</LineChart>
			</ChartContainer>
		</div>
	)
}
